import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["indicator", "bar", "label", "input", "confirmation", "feedback",
    "requirementLength", "requirementUppercase", "requirementLowercase",
    "requirementNumber", "requirementSpecial", "requirementMatch"]
  static values = { minimumLength: Number }

  connect() {
    if (this.hasInputTarget && this.hasMinimumLengthValue) {
      this.checkStrength({ target: this.inputTarget })
    }
  }

  checkStrength(event) {
    if (!this.hasMinimumLengthValue) return

    const password = event.target.value
    const checks = {
      length: this.meetsMinimum(password),
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    }
    if (this.hasIndicatorTarget) {
      if (password.length === 0) this.indicatorTarget.classList.add("d-none")
      else this.indicatorTarget.classList.remove("d-none")
    }

    const strength = this.calculateStrength(password)
    const level = !checks.length ? "weak" : strength >= 4 ? "strong" : strength >= 2 ? "medium" : "weak"
    if (this.hasBarTarget) {
      this.barTarget.classList.remove("weak", "medium", "strong")
      this.barTarget.classList.add(level)
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = password.length === 0 ? "" : !checks.length ? "Too short" :
        level[0].toUpperCase() + level.slice(1)
    }

    this.updateCheck("requirementLength", checks.length)
    for (const key of ["uppercase", "lowercase", "number", "special"]) {
      this.updateCheck(`requirement${key[0].toUpperCase()}${key.slice(1)}`, checks[key], "Included", "Not included")
    }
    // Clearing the primary field must also clear a previously successful match.
    this.checkMatch()
  }

  checkMatch() {
    if (this.hasInputTarget && this.hasConfirmationTarget) {
      const matches = this.inputTarget.value.length > 0 &&
        this.inputTarget.value === this.confirmationTarget.value
      this.updateCheck("requirementMatch", matches, "Match", "Do not match")
    }
    this.updateFeedback()
  }

  meetsMinimum(password) {
    // Ruby String#length counts characters rather than UTF-16 code units.
    return Array.from(password).length >= this.minimumLengthValue
  }

  calculateStrength(password) {
    let strength = this.meetsMinimum(password) ? 1 : 0
    // Twelve characters is an optional strength suggestion, not an acceptance rule.
    if (Array.from(password).length >= 12) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++
    return strength
  }

  updateCheck(target, met, success = "Met", failure = "Not met") {
    if (!this[`has${target[0].toUpperCase()}${target.slice(1)}Target`]) return

    const element = this[`${target}Target`]
    const icon = element.querySelector("i")
    const status = element.querySelector("[data-check-status]")
    if (icon) icon.className = met ? "fas fa-check-circle text-success" : "fas fa-times-circle text-danger"
    if (status) status.textContent = met ? success : failure
  }

  updateFeedback() {
    if (!this.hasFeedbackTarget || !this.hasInputTarget || !this.hasMinimumLengthValue) return

    const password = this.inputTarget.value
    if (password.length === 0) {
      this.feedbackTarget.textContent = "Enter a password."
    } else if (!this.meetsMinimum(password)) {
      this.feedbackTarget.textContent = `Password requirements not met: use at least ${this.minimumLengthValue} characters.`
    } else if (this.hasConfirmationTarget && password !== this.confirmationTarget.value) {
      this.feedbackTarget.textContent = "Password length met. Confirmation does not match."
    } else {
      this.feedbackTarget.textContent = "Password requirements met. Strength suggestions are optional."
    }
  }

  toggleVisibility(event) {
    const button = event.currentTarget
    const inputGroup = button.closest(".input-group")
    const input = inputGroup.querySelector("input[type='password'], input[type='text']")
    const icon = button.querySelector("i")
    const show = input.type === "password"

    input.type = show ? "text" : "password"
    button.setAttribute("aria-label", `${show ? "Hide" : "Show"} ${button.dataset.passwordLabel || "password"}`)
    button.setAttribute("aria-pressed", String(show))
    if (icon) {
      icon.classList.remove(show ? "fa-eye" : "fa-eye-slash")
      icon.classList.add(show ? "fa-eye-slash" : "fa-eye")
    }
  }
}
